import { useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

// Reusable pagination footer. Disabled controls surface the current position.
export function DataPagination({
  page = 1,
  pageCount = 1,
  total = 0,
  pageSize = 25,
  onPageChange,
  className,
  testId = "pagination",
}) {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const canPrev = page > 1;
  const canNext = page < pageCount;

  const pages = useMemo(() => {
    const windowSize = 5;
    let start = Math.max(1, page - Math.floor(windowSize / 2));
    const end = Math.min(pageCount, start + windowSize - 1);
    start = Math.max(1, end - windowSize + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [page, pageCount]);

  return (
    <div
      data-testid={testId}
      className={cn(
        "flex flex-col items-center justify-between gap-3 rounded-xl border border-border bg-card px-5 py-3 shadow-soft sm:flex-row",
        className
      )}
    >
      <p className="text-sm text-muted-foreground">
        Showing <span className="font-medium text-foreground">{from}</span>–
        <span className="font-medium text-foreground">{to}</span> of{" "}
        <span className="font-medium text-foreground">{total}</span>
      </p>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1 px-2"
          disabled={!canPrev}
          onClick={() => canPrev && onPageChange?.(page - 1)}
          data-testid="pagination-prev"
        >
          <ChevronLeft className="h-4 w-4" />
          Prev
        </Button>
        {pages.map((p) => (
          <Button
            key={p}
            variant={p === page ? "default" : "ghost"}
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => onPageChange?.(p)}
            data-testid={`pagination-page-${p}`}
          >
            {p}
          </Button>
        ))}
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1 px-2"
          disabled={!canNext}
          onClick={() => canNext && onPageChange?.(page + 1)}
          data-testid="pagination-next"
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
